import { z } from "zod";

import { Form } from "@/components/ui/form";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Input,
} from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  formatDate,
  parseIntValue,
  parseToDateValue,
} from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { useTranslation } from "react-i18next";

import { useEditForm } from "@/features/Edit/hooks";
import { Tables } from "@/gen/tableNames/tables";

const FormSchema = z.object({
  strategy_id: z.coerce.number(),
  portfolio_id: z.coerce.number().optional().nullable(),
  credit_quality_step: z.preprocess(parseIntValue, z.number().int().nullable()),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  book_value: z.coerce.number().optional().nullable(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

const formSchemaFields = FormSchema.keyof().enum;

export function E2DynamicDataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  const { t } = useTranslation("tables");

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${Tables.E2_DYNAMIC_DATA}.title`)} {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_id}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.strategy_id`)}
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.portfolio_id}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.portfolio_id`)}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.date`)}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.book_value}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.book_value`)}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.credit_quality_step}
                    label={t(
                      `${Tables.E2_DYNAMIC_DATA}.columns.credit_quality_step`,
                    )}
                    control={form.control}
                  />
                </div>
                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={t("technical_info.id", {
                      defaultValue: "Id",
                    })}
                    value={id.toString()}
                    readOnly
                  />
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.created_at`)}
                    value={formatDate(form.watch(formSchemaFields.created_at))}
                    readOnly
                  />
                  <Input
                    radius="full"
                    variant="underlined"
                    labelPlacement="outside"
                    size="sm"
                    type="text"
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.updated_at`)}
                    value={formatDate(form.watch(formSchemaFields.updated_at))}
                    readOnly
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [], {}, table_name)
              : t("no_changes")}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
